import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {Bindings} from "data/constants/bindings";
import {RoundStatus, TournamentStatus} from "data/enums";
import {findIndex, first, flattenDeep, includes, isEqual, last} from "lodash";

export interface IRound {
	date: string;
	id: number;
	name: string;
	status: RoundStatus;
	sessions: ISession[];
	tiebreakerPoints: number;
}

export interface ISession {
	id: number;
	gamedayId: number;
	name: string;
	status: RoundStatus;
	startDate: string;
	endDate: string;
	longestDriveHole: string | null;
	nearestPinHole: string | null;
	tournaments: ITournament[];
	golfers: IGolfersData[];
}

export interface IGolfersData {
	usa: number[];
	europe: number[];
}

export interface ITournament {
	id: number | null;
	feedCode: string | null;
	status: TournamentStatus;
	teeTime: string;
	isPostponed: boolean;
	golfers: number[];
}

export interface IRoundsStore {
	get getIsLoading(): boolean;
	get list(): IRound[];
	get rounds(): ISession[];
	get scheduleRounds(): IRound[];
	get currentRound(): IRound | undefined;
	get scoreRound(): IRound | null;
	get selectedRoundId(): number;
	get userTeamSelectedRoundId(): number;
	get actualRound(): IRound | undefined;
	get completedRounds(): IRound[];
	get selectedRound(): IRound | undefined;
	get userTeamSelectedRound(): IRound | undefined;
	get selectedRoundSessions(): ISession[];
	get userTeamSelectedRoundSessions(): ISession[];
	get selectedSessionId(): number;
	get userTeamsSelectedSessionId(): number;
	get selectedSession(): ISession | undefined;
	get userTeamSelectedSession(): ISession | undefined;
	get isPMSession(): boolean;
	get isPMSessionNotScheduled(): boolean;
	get firstSession(): ISession | undefined;
	get firstRound(): IRound | undefined;
	get selectedRoundIndex(): number;
	get lastCompleteOrPlayingSession(): ISession | undefined;

	setSelectedRoundId(roundId: number): void;
	setSelectedRoundSessions(): void;
	setSelectedSessionId(sessionId: number): void;
	setUserTeamSelectedRoundId(roundId: number): void;
	setUserTeamSelectedRoundSessions(): void;
	setUserTeamSelectedSessionId(sessionId: number): void;
	fetchRounds(): Promise<void>;
	getRoundById(roundId: number): ISession | null;
}

@injectable()
export class RoundsStore implements IRoundsStore {
	@observable private _isLoading: boolean = false;

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@observable private _list: IRound[] = [];
	@observable private _selectedRoundId: number = 0;
	@observable private _selectedRoundSessions: ISession[] = [];
	@observable private _selectedSessionId: number = 0;

	@observable private _userTeamSelectedRoundId: number = 0;
	@observable private _userTeamSelectedRoundSessions: ISession[] = [];
	@observable private _userTeamSelectedSessionId: number = 0;

	get list() {
		return this._list;
	}

	get rounds() {
		return this.list.reduce((acc, round) => {
			return [...acc, ...round.sessions];
		}, [] as ISession[]);
	}

	get selectedRoundId() {
		return this._selectedRoundId;
	}

	get userTeamSelectedRoundId() {
		return this._userTeamSelectedRoundId;
	}

	get actualRound() {
		return (
			this._list.find(({status}) =>
				[RoundStatus.Playing, RoundStatus.Scheduled].includes(status)
			) || last(this._list)
		);
	}

	get scheduleRounds() {
		return this._list.filter((e) => e.status === RoundStatus.Scheduled);
	}

	get completedRounds() {
		return this._list.filter((e) => e.status === RoundStatus.Complete);
	}

	get getIsLoading(): boolean {
		return this._isLoading;
	}

	get currentRound() {
		return this.activeRound || first(this.scheduleRounds) || last(this.list);
	}

	get scoreRound() {
		return this.activeRound || last(this.completedRounds) || null;
	}

	private get activeRound() {
		return this.list.find((e) => e.status === RoundStatus.Playing);
	}

	get selectedRound() {
		return this.list.find((round) => round.id === this._selectedRoundId);
	}

	get userTeamSelectedRound() {
		return this.list.find((round) => round.id === this._userTeamSelectedRoundId);
	}

	get selectedRoundSessions() {
		return this._selectedRoundSessions;
	}

	get userTeamSelectedRoundSessions() {
		return this._userTeamSelectedRoundSessions;
	}

	get selectedSessionId() {
		return this._selectedSessionId;
	}

	get userTeamsSelectedSessionId() {
		return this._userTeamSelectedSessionId;
	}

	get selectedSession() {
		return this._selectedRoundSessions.find(
			(session) => session.id === this._selectedSessionId
		);
	}

	get userTeamSelectedSession() {
		return this._userTeamSelectedRoundSessions.find(
			(session) => session.id === this._userTeamSelectedSessionId
		);
	}

	get isPMSession() {
		const sessionIndex = findIndex(
			this._selectedRoundSessions,
			(it) => it.id === this._selectedSessionId
		);

		return sessionIndex > 0;
	}

	get isPMSessionNotScheduled() {
		return includes(
			[RoundStatus.Playing, RoundStatus.Complete],
			this.selectedRoundSessions[1].status
		);
	}

	get firstSession() {
		return first(this._selectedRoundSessions);
	}

	get firstRound() {
		return first(this._list);
	}

	get selectedRoundIndex() {
		return findIndex(this._list, (el) => el.id === this.selectedRoundId);
	}

	get lastCompleteOrPlayingSession() {
		const allSessions = this._list.map((round) => round.sessions);
		const completeOrPlayingSessions = flattenDeep(allSessions).filter((el) =>
			includes([RoundStatus.Playing, RoundStatus.Complete], el.status)
		);
		return last(completeOrPlayingSessions);
	}

	@action setSelectedRoundId(roundId: number) {
		this._selectedRoundId = roundId;
	}

	@action setSelectedRoundSessions() {
		this._selectedRoundSessions = this.selectedRound?.sessions || [];
	}

	@action setSelectedSessionId(sessionId: number) {
		this._selectedSessionId = sessionId;
	}

	@action setUserTeamSelectedRoundId(roundId: number) {
		this._userTeamSelectedRoundId = roundId;
	}

	@action setUserTeamSelectedRoundSessions() {
		this._userTeamSelectedRoundSessions = this.userTeamSelectedRound?.sessions || [];
	}

	@action setUserTeamSelectedSessionId(sessionId: number) {
		this._userTeamSelectedSessionId = sessionId;
	}

	getRoundById(roundId: number) {
		return this.rounds.find((session) => isEqual(session.id, roundId)) || null;
	}

	@action
	async fetchRounds() {
		const {data} = await this._jsonProvider.rounds();

		runInAction(() => {
			this._list = data;

			this.setSelectedRoundId(this.actualRound?.id ?? 1);
			this.setSelectedRoundSessions();
			this.setSelectedSessionId(this.selectedRoundSessions[0].id ?? 0);
		});
	}
}
